import React, { useState, useEffect } from "react";
import { Container, Row, Col, Table, Form } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import { FaSearch, FaSort } from "react-icons/fa";
import { subscribedUsersData } from "../../../../../services/AdminService";
import Pagination from "react-js-pagination";
import Utils from "../../../../../utils";

const Subscribers = () => {
  const lengths = Utils.tableShowLengths();

  const [loading, setLoading] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" })
  const [totalData, setTotalData] = useState(0);

  const [dataParams, setDataParams] = useState({
    limit: lengths[0],
    page: 1,
    status_id: 2,
    search: "",
    sender_id: "",
    envelope_id: "",
    order_column: 'created_at',
    order_by: false,
    is_effect: false,
    selectedPlan: "All"
  });


  // Handler for pagination-related changes
  const handleData = (e, type) => {
    let params = { ...dataParams };
    if (type === 'pagination') {
      params.page = e;
    } else if (type === 'limit') {
      params.limit = parseInt(e.target.value);
      params.page = 1; // Reset to first page when limit changes
    }
    setDataParams(params);
  };

  // Fetch users when plan or dataParams changes
  useEffect(() => {
    setLoading(true);
    const fetchUsers = async () => {
      try {
        const filter = dataParams.selectedPlan === "All" ? "all" : dataParams.selectedPlan.toLowerCase();
        const response = await subscribedUsersData({ filter });
        const users = response?.data?.users || [];
        setAllUsers(users);
        setFilteredUsers(users);
        setTotalData(users.length); // Update total count
      } catch (error) {
        console.error("Error fetching users:", error);
        setAllUsers([]);
        setFilteredUsers([]);
        setTotalData(0);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [dataParams.selectedPlan]);

  // Filter users based on search term
  useEffect(() => {
    const results = allUsers.filter(user =>
      searchTerm
        ? `${user.first_name || ""} ${user.last_name || ""} ${user.email || ""} ${user.company_name || ""} ${user.subscription_type || ""}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
        : true
    );
    setFilteredUsers(results);
    setTotalData(results.length);
    if (dataParams.page > Math.ceil(results.length / dataParams.limit)) {
      setDataParams(prev => ({ ...prev, page: 1 }));
    }
  }, [searchTerm, allUsers, dataParams.limit]);

  // Sort and paginate users
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aValue = String(a[sortConfig.key] || "");
    const bValue = String(b[sortConfig.key] || "");
    return sortConfig.direction === "asc"
      ? aValue.localeCompare(bValue)
      : bValue.localeCompare(aValue);
  });

  const paginatedUsers = sortedUsers.slice(
    (dataParams.page - 1) * dataParams.limit,
    dataParams.page * dataParams.limit
  );

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc",
    });
    setDataParams(prev => ({ ...prev, order_column: key, order_by: sortConfig.direction !== "asc" }));
  };

  return (
    <div className="custom_container px-0">
      {loading && <div className="page-loading">
        <img src="/images/loader.gif" alt="loader" />
      </div>}
      <Container fluid className="p-4 mt-4 rounded-2 bg-light">
        <Row className="mb-4">
          <Col>
            <h2 className="text-dark fw-bold">Subscription Management</h2>
          </Col>
        </Row>

        <Row className="mb-4 align-items-center">
          <Col md={6} className="mb-2 mb-md-0">
            <div className="position-relative">
              <FaSearch className="position-absolute top-50 translate-middle-y ms-2 text-muted" />
              <Form.Control
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="ps-5 rounded"
              />
            </div>
          </Col>
          <Col md={4}>
            <Form.Select
              value={dataParams.selectedPlan || "All"}
              onChange={(e) => setDataParams(prev => ({ ...prev, selectedPlan: e.target.value }))}
            >
              <option value="All">All Plans</option>
              <option value="Free">Free Plan</option>
              <option value="Team">Team Plan</option>
              <option value="Pro">Pro Plan</option>
            </Form.Select>
          </Col>
        </Row>

        {loading ? (
          <Row><Col className="text-center">Loading...</Col></Row>
        ) : (
          <>
            <style>
              {`
              .table-responsive-wrapper {
                overflow-x: auto;
                max-width: 100%;
                -webkit-overflow-scrolling: touch;
              }
              .table-responsive-wrapper::-webkit-scrollbar {
                height: 2px !important;
              }
              .table-responsive-wrapper::-webkit-scrollbar-thumb {
                background-color: #888;
                border-radius: 2px;
              }
              .table-responsive-wrapper::-webkit-scrollbar-thumb:hover {
                background-color: #555;
              }
              .table th, .table td {
                white-space: nowrap;
              }
            `}
            </style>
            <div className="table-responsive-wrapper">
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>#</th>
                    {[
                      "first_name",
                      "last_name",
                      "email",
                      "company_name",
                      "subscription_type",
                      "total_storage(MB)",
                      "total_sleeve",
                      "total_user",
                      "last_login",
                      "created_at",
                    ].map((key) => (
                      <th key={key}>
                        {key.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                        <FaSort className="ms-1 cursor-pointer" onClick={() => handleSort(key)} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedUsers.length ? (
                    paginatedUsers.map((user, index) => (
                      <tr key={user.id || index}>
                        <td>{(dataParams.page - 1) * dataParams.limit + index + 1}</td>
                        <td>{user.first_name || "-"}</td>
                        <td>{user.last_name || "-"}</td>
                        <td>{user.email || "-"}</td>
                        <td>{user.company_name || "-"}</td>
                        <td>{user.subscription_type || "-"}</td>
                        <td>{user.total_storage_mb ?? "-"}</td>
                        <td>{user.total_sleeve ?? "-"}</td>
                        <td>{user.total_user ?? "-"}</td>
                        <td>
                          {user.last_login ? new Date(user.last_login).toLocaleDateString() : "-"}
                        </td>
                        <td>
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : "-"}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="11" className="text-center">
                        No users found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            <div className="table_footer_wrap">
              <div className="d-flex align-items-center my-4">
                <span className="me-2 text-dark">Show</span>
                <select
                  className="form-select"
                  value={dataParams.limit}
                  onChange={(e) => handleData(e, 'limit')}
                  aria-label="Default select example"
                >
                  {lengths && lengths.map((item, i) => <option value={item} key={i}>{item}</option>)}
                </select>
              </div>
              <div className="pagination mb-4">
                <Pagination
                  activePage={dataParams.page}
                  itemsCountPerPage={dataParams.limit}
                  totalItemsCount={totalData}
                  pageRangeDisplayed={5}
                  onChange={(e) => handleData(e, 'pagination')}
                />
              </div>
            </div>
          </>
        )}
      </Container>
    </div>
  );
};

export default Subscribers;